import {
  PROPERTY_PROFILE_SECTIONS,
  profileConditionHolds,
  type ProfileFieldCondition,
  type ProfileFieldDef,
  type ProfileValueSource,
  type PropertyProfileSectionDef,
  type PropertyProfileResponse,
  type PropertyProfileSectionId,
  type PropertyProfileV1,
} from '@natalan/capehost-contracts'

/**
 * Reading and editing the property profile from its one declaration,
 * `PROPERTY_PROFILE_SECTIONS` (CAP-1448): labels, helper text, options and
 * conditions all come from there, never retyped here.
 *
 * The typed section shapes in the contracts are per-section mapped types;
 * every helper here walks any section, so it works on the plain-record view
 * of them (`StoredFields`, `InputFields`) and lets the declaration say what
 * each key holds.
 */

/** Every section, typed for generic walking. */
export const PROFILE_SECTIONS: readonly PropertyProfileSectionDef[] = PROPERTY_PROFILE_SECTIONS

export function sectionDef(sectionId: PropertyProfileSectionId): PropertyProfileSectionDef {
  const def = PROFILE_SECTIONS.find(section => section.id === sectionId)
  if (!def) throw new Error(`Unknown profile section ${sectionId}`)
  return def
}

/** A stored answer: the value, whose it is, and when it was given. */
export interface ProfileLeafValue {
  value: unknown
  source: ProfileValueSource
  updated_at: string
}

/** Stored fields of a section or group item: leaves, groups (arrays of items) or null. */
export type StoredFields = Record<string, unknown>

/** Bare values, as a save request carries them. */
export type InputFields = Record<string, unknown>

export type StoredSection = {
  status: 'not_started' | 'in_progress' | 'complete'
  changed_since_review: boolean
  updated_at: string | null
  updated_by: string | null
  fields: StoredFields
}

export function storedSection(
  profile: PropertyProfileV1,
  sectionId: PropertyProfileSectionId
): StoredSection {
  return (profile.sections as unknown as Record<string, StoredSection>)[sectionId]
}

export function isLeaf(value: unknown): value is ProfileLeafValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    'value' in value &&
    'source' in value
  )
}

/** A group's stored items, or null when the question is unanswered. */
export function groupItems(value: unknown): (StoredFields & { id: string })[] | null {
  return Array.isArray(value) ? (value as (StoredFields & { id: string })[]) : null
}

/** The bare value behind a stored field, for conditions and for editing. */
export function bare(value: unknown): unknown {
  return isLeaf(value) ? value.value : value
}

/**
 * Whether a field applies, by its `when` conditions: a condition with a
 * `section` reads that section's top-level field, one without reads the
 * same container (the section, or the group item the field sits in).
 * `container` and `profile` hold either stored or bare values.
 */
export function fieldApplies(
  field: { when?: readonly ProfileFieldCondition[] },
  container: Record<string, unknown>,
  sectionFieldsOf: (sectionId: string) => Record<string, unknown> | undefined
): boolean {
  if (!field.when?.length) return true
  return field.when.every(condition => {
    const source = condition.section ? sectionFieldsOf(condition.section) : container
    return profileConditionHolds(condition, bare(source?.[condition.field]))
  })
}

/** Looks up any section's stored fields in a profile, for cross-section conditions. */
export function profileSectionFields(profile: PropertyProfileV1) {
  return (sectionId: string) =>
    (profile.sections as unknown as Record<string, StoredSection | undefined>)[sectionId]?.fields
}

function formatMonth(value: string): string {
  const [year, month] = value.split('-').map(Number)
  if (!year || !month) return value
  return new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' }).format(
    new Date(year, month - 1, 1)
  )
}

/** One answer as text, from its declaration; never called for a group. */
export function formatAnswer(field: ProfileFieldDef, value: unknown): string {
  if (value === null || value === undefined) return 'Not answered'
  switch (field.kind) {
    case 'text':
      return String(value)
    case 'number':
      return field.unit ? `${value} ${field.unit}` : String(value)
    case 'boolean':
      return value ? 'Yes' : 'No'
    case 'choice':
      return field.options.find(option => option.value === value)?.label ?? String(value)
    case 'multi_choice': {
      const values = Array.isArray(value) ? value : []
      if (values.length === 0) return 'None'
      return values
        .map(v => field.options.find(option => option.value === v)?.label ?? String(v))
        .join(', ')
    }
    case 'month':
      return formatMonth(String(value))
    case 'group':
      return Array.isArray(value) ? `${value.length} ${field.item_label.toLowerCase()}(s)` : ''
  }
}

/** Stored fields to bare values, recursively, keeping every declared key. */
export function toInputFields(
  fields: readonly ProfileFieldDef[],
  stored: StoredFields | null | undefined
): InputFields {
  const out: InputFields = {}
  for (const field of fields) {
    const value = stored?.[field.key]
    if (field.kind === 'group') {
      const items = groupItems(value)
      out[field.key] = items
        ? items.map(item => ({ id: item.id, ...toInputFields(field.fields, item) }))
        : null
    } else {
      out[field.key] = isLeaf(value) ? value.value : null
    }
  }
  return out
}

/** An empty group item, every field unanswered, with a fresh client-side id. */
export function emptyGroupItem(fields: readonly ProfileFieldDef[]): InputFields & { id: string } {
  const item: InputFields & { id: string } = { id: newGroupItemId() }
  for (const field of fields) item[field.key] = null
  return item
}

function newGroupItemId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `item-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Ordinary text is trimmed before saving, as the contracts ask of the UIs;
 * a sensitive value (a door code, a wifi password) is sent exactly as typed.
 * Blank text is no answer.
 */
export function normalizeInputFields(
  fields: readonly ProfileFieldDef[],
  input: InputFields
): InputFields {
  const out: InputFields = {}
  for (const field of fields) {
    const value = input[field.key]
    if (field.kind === 'group') {
      out[field.key] = Array.isArray(value)
        ? (value as (InputFields & { id: string })[]).map(item => ({
            id: item.id,
            ...normalizeInputFields(field.fields, item),
          }))
        : null
    } else if (field.kind === 'text' && typeof value === 'string') {
      const text = field.sensitive ? value : value.trim()
      out[field.key] = /\S/.test(text) ? text : null
    } else {
      out[field.key] = value ?? null
    }
  }
  return out
}

type Item = InputFields & { id: string }

function same(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null)
}

/**
 * Lays the answers I changed (`mine` against `opened`) over `theirs`, the
 * section someone else saved meanwhile: a leaf I did not touch keeps theirs,
 * and a repeating group merges item by item (by id), so their edit to
 * another item, or an item they added, survives mine.
 */
export function mergeOverTheirs(
  fields: readonly ProfileFieldDef[],
  opened: InputFields,
  mine: InputFields,
  theirs: InputFields
): InputFields {
  const out: InputFields = { ...theirs }
  for (const field of fields) {
    const was = opened[field.key]
    const now = mine[field.key]
    if (same(was, now)) continue
    const their = theirs[field.key]
    if (
      field.kind !== 'group' ||
      !Array.isArray(was) ||
      !Array.isArray(now) ||
      !Array.isArray(their)
    ) {
      out[field.key] = now
      continue
    }
    const openedById = new Map((was as Item[]).map(item => [item.id, item]))
    const mineById = new Map((now as Item[]).map(item => [item.id, item]))
    const merged: Item[] = []
    for (const theirItem of their as Item[]) {
      const openedItem = openedById.get(theirItem.id)
      const mineItem = mineById.get(theirItem.id)
      if (openedItem && !mineItem) continue // I removed it
      if (openedItem && mineItem) {
        merged.push({
          ...(mergeOverTheirs(field.fields, openedItem, mineItem, theirItem) as Item),
          id: theirItem.id,
        })
      } else {
        merged.push(theirItem)
      }
    }
    for (const mineItem of now as Item[]) {
      if (openedById.has(mineItem.id)) {
        // An item I edited that they removed comes back only if I changed it.
        const inTheirs = (their as Item[]).some(item => item.id === mineItem.id)
        if (!inTheirs && !same(openedById.get(mineItem.id), mineItem)) merged.push(mineItem)
      } else {
        merged.push(mineItem) // I added it
      }
    }
    out[field.key] = merged
  }
  return out
}

/** Labels for the listing fields admin-api compares with Uplisting (CAP-1462). */
const LISTING_FIELD_LABELS: Record<string, string> = {
  address: 'Address',
  display_name: 'Name',
  bedrooms: 'Bedrooms',
  beds: 'Beds',
  bathrooms: 'Bathrooms',
  maximum_capacity: 'Most guests',
}

export interface ListingDiffRow {
  field: string
  label: string
  profile: string
  uplisting: string
}

/**
 * The response's `listing_diff` (live properties only: where Uplisting and
 * the owner's answers disagree), labelled for the "Uplisting disagrees" panel.
 */
export function listingDiffRows(
  listingDiff: PropertyProfileResponse['listing_diff']
): ListingDiffRow[] {
  return (listingDiff ?? []).map(row => ({
    field: row.field,
    label: LISTING_FIELD_LABELS[row.field] ?? row.field,
    profile: String(row.profile),
    uplisting: String(row.uplisting),
  }))
}

/** The required-but-unanswered field paths of one section, as a set of `<field>` or `<group>.<itemId>.<field>`. */
export function missingInSection(
  requiredMissing: readonly string[],
  sectionId: PropertyProfileSectionId
): Set<string> {
  const prefix = `${sectionId}.`
  return new Set(
    requiredMissing.filter(path => path.startsWith(prefix)).map(path => path.slice(prefix.length))
  )
}

export const SOURCE_LABELS: Record<ProfileValueSource, string> = {
  owner: 'Owner',
  ops: 'Ops',
  uplisting: 'Uplisting',
}

export const SECTION_STATUS_LABELS: Record<StoredSection['status'], string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  complete: 'Complete',
}

export const PROFILE_STATUS_LABELS: Record<PropertyProfileV1['status'], string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  reviewed: 'Reviewed',
}

/** One field's change between two stored versions of a section, for the revisions drawer. */
export interface FieldChange {
  key: string
  label: string
  before: string
  after: string
}

const MASK = '••••••'

/**
 * A stored answer as history text. A secret is never written out: its
 * changes show as a mask, so an old door code is not on screen. Who gave the
 * answer is part of it, so a save that only moved an answer from the owner
 * to ops still shows.
 */
function describeStored(field: ProfileFieldDef, value: unknown): string {
  if (field.kind === 'group') {
    const items = groupItems(value)
    if (!items) return 'Not answered'
    if (items.length === 0) return 'None'
    return items
      .map((item, index) => {
        const parts = field.fields
          .map(sub => {
            const stored = item[sub.key]
            if (stored === null || stored === undefined) return null
            if (isLeaf(stored) && (stored.value === null || stored.value === undefined)) return null
            return `${sub.label}: ${describeStored(sub, stored)}`
          })
          .filter(Boolean)
        return `${field.item_label} ${index + 1}${parts.length ? ` (${parts.join('; ')})` : ''}`
      })
      .join(', ')
  }
  if (!isLeaf(value) || value.value === null || value.value === undefined) return 'Not answered'
  const text = field.sensitive ? MASK : formatAnswer(field, value.value)
  return `${text} (${SOURCE_LABELS[value.source] ?? value.source})`
}

/** Whether a secret's value changed, compared without ever formatting it. */
function sameSecret(a: unknown, b: unknown): boolean {
  return (
    JSON.stringify(isLeaf(a) ? [a.value, a.source] : a) ===
    JSON.stringify(isLeaf(b) ? [b.value, b.source] : b)
  )
}

/**
 * What changed between two revisions of a section, by field. `before` and
 * `after` are the stored section JSON (kept loose: history outlives schema
 * versions), so a key the declaration does not know is shown by its name.
 */
export function sectionChanges(
  sectionId: PropertyProfileSectionId,
  before: Record<string, unknown> | null,
  after: Record<string, unknown>
): FieldChange[] {
  const def = sectionDef(sectionId)
  const beforeFields = (before?.fields ?? {}) as StoredFields
  const afterFields = (after.fields ?? {}) as StoredFields
  const changes: FieldChange[] = []
  const keys = new Set([...Object.keys(beforeFields), ...Object.keys(afterFields)])
  for (const key of keys) {
    const field = def.fields.find(f => f.key === key)
    const was = beforeFields[key] ?? null
    const now = afterFields[key] ?? null
    if (field?.sensitive) {
      if (!sameSecret(was, now)) {
        changes.push({
          key,
          label: field.label,
          before: describeStored(field, was),
          after: `${describeStored(field, now)}, changed`,
        })
      }
    } else if (field) {
      const b = describeStored(field, was)
      const a = describeStored(field, now)
      if (b !== a) changes.push({ key, label: field.label, before: b, after: a })
    } else if (JSON.stringify(was) !== JSON.stringify(now)) {
      changes.push({ key, label: key, before: JSON.stringify(was), after: JSON.stringify(now) })
    }
  }
  return changes
}
